import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, UserMinus, Users, Search, Check, ArrowUpRight, Plus, Clock, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { CommandPalette } from "@/components/CommandPalette";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { DirectSplitDialog } from "@/components/DirectSplitDialog";
import { api, currencySymbol, formatAmount } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Friends() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState({ friends: [], net: 0, incoming: 0, outgoing: 0, currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [splitWith, setSplitWith] = useState(null);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });

  const loadRequests = async () => {
    try {
      const { data } = await api.get("/friend-requests");
      setRequests(data);
    } catch { /* silent */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/friends/summary");
      setSummary(data);
      loadRequests();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setSearching(true);
      try {
        const { data } = await api.get(`/users/search?q=${encodeURIComponent(q.trim())}`);
        const pendingIds = new Set((requests.outgoing || []).map((r) => r.to_user_id));
        setResults(data.map((u) => ({ ...u, request_sent: pendingIds.has(u.id) })));
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, requests.outgoing]);

  const add = async (u) => {
    try {
      const { data } = await api.post(`/friends/${u.id}`);
      if (data?.status === "pending") {
        toast.success(`Request sent to ${u.name}`);
        setResults((r) => r.map((x) => x.id === u.id ? { ...x, request_sent: true } : x));
      } else {
        toast.success(`Added ${u.name}`);
        setResults((r) => r.map((x) => x.id === u.id ? { ...x, is_friend: true } : x));
      }
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (u) => {
    try { await api.delete(`/friends/${u.id}`); toast.success(`Removed ${u.name}`); load(); } catch { toast.error("Failed"); }
  };
  const acceptReq = async (rid, name) => {
    try { await api.post(`/friend-requests/${rid}/accept`); toast.success(`Added ${name}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const declineReq = async (rid) => {
    try { await api.post(`/friend-requests/${rid}/decline`); toast.success("Declined"); load(); }
    catch { toast.error("Failed"); }
  };
  const cancelReq = async (rid) => {
    try { await api.delete(`/friend-requests/${rid}`); toast.success("Cancelled"); load(); }
    catch { toast.error("Failed"); }
  };

  const currency = summary.currency || user?.currency || "USD";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar onOpenCmdK={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} setOpen={setCmdkOpen} />
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">/ Friends</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mb-8">Your crew</h1>

        {/* Summary card */}
        <div
          className="rounded-2xl border p-6 mb-6 relative overflow-hidden"
          style={{ borderColor: summary.net >= 0 ? "rgba(0,255,102,0.3)" : "rgba(255,59,48,0.3)", background: summary.net >= 0 ? "rgba(0,255,102,0.04)" : "rgba(255,59,48,0.04)" }}
          data-testid="friends-summary-card"
        >
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Net balance</div>
          <div className="flex items-end gap-4 flex-wrap">
            <AnimatedNumber value={summary.net} currency={currency} withSign className={`font-mono text-4xl sm:text-5xl font-semibold ${summary.net >= 0 ? "brand-text" : "text-red-400"}`} />
            <div className="flex items-center gap-5 text-xs font-mono uppercase tracking-wider pb-2">
              <span><span className="brand-text">+{formatAmount(summary.incoming, currency)}</span> <span className="text-muted-foreground">incoming</span></span>
              <span className="h-3 w-px bg-border" />
              <span><span className="text-red-400">-{formatAmount(summary.outgoing, currency)}</span> <span className="text-muted-foreground">outgoing</span></span>
            </div>
          </div>
        </div>

        {/* Pending requests */}
        {(requests.incoming.length > 0 || requests.outgoing.length > 0) && (
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.03] p-6 mb-6" data-testid="friend-requests-panel">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-amber-300 mb-4 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} /> Pending requests
              <span className="text-muted-foreground">· {requests.incoming.length + requests.outgoing.length}</span>
            </div>
            {requests.incoming.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Incoming</div>
                <div className="divide-y divide-border">
                  {requests.incoming.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2.5" data-testid={`incoming-req-${r.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{r.from_user_name}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">wants to be your friend</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button size="sm" onClick={() => acceptReq(r.id, r.from_user_name)} className="brand-bg text-black hover:opacity-90 h-8" data-testid={`accept-req-${r.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Accept
                        </Button>
                        <button onClick={() => declineReq(r.id)} className="h-8 w-8 rounded-md border border-border hover:bg-foreground/5 transition flex items-center justify-center" data-testid={`decline-req-${r.id}`} aria-label="Decline">
                          <X className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {requests.outgoing.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Sent by you</div>
                <div className="divide-y divide-border">
                  {requests.outgoing.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-2.5" data-testid={`outgoing-req-${r.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm truncate">{r.to_user_name}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">pending · expires in 7 days</div>
                      </div>
                      <button onClick={() => cancelReq(r.id)} className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-red-400 h-8 px-3 rounded-md border border-border" data-testid={`cancel-req-${r.id}`}>
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="rounded-2xl border border-border bg-card/40 p-6 mb-6" data-testid="friends-search-panel">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Find people</div>
          <div className="relative">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 h-11 bg-muted border-border" data-testid="friends-search-input" />
          </div>
          {q.trim().length >= 2 && (
            <div className="mt-4 divide-y divide-border" data-testid="friends-search-results">
              {searching ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Searching…</div>
              ) : results.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground" data-testid="friends-no-results">No users found</div>
              ) : (
                results.map((u) => (
                  <div key={u.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-10 w-10 rounded-full bg-foreground/5 border border-border flex items-center justify-center text-lg">{u.avatar_emoji || "🙂"}</span>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                      </div>
                    </div>
                    {u.is_friend ? (
                      <span className="text-[10px] font-mono uppercase tracking-wider brand-text flex items-center gap-1"><Check className="h-3 w-3" strokeWidth={2} /> Friend</span>
                    ) : u.request_sent ? (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-amber-400 flex items-center gap-1" data-testid={`request-sent-${u.id}`}><Clock className="h-3 w-3" strokeWidth={2} /> Request sent</span>
                    ) : (
                      <Button size="sm" onClick={() => add(u)} className="brand-bg text-black hover:opacity-90 h-8" data-testid={`add-friend-btn-${u.id}`}>
                        <UserPlus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> Add
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Friends list */}
        <div className="rounded-2xl border border-border bg-card/40 p-6" data-testid="friends-list-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">My friends</div>
            <div className="text-xs font-mono text-muted-foreground">{summary.friends.length}</div>
          </div>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : summary.friends.length === 0 ? (
            <div className="py-10 text-center" data-testid="friends-empty">
              <div className="h-12 w-12 mx-auto rounded-2xl bg-foreground/5 border border-border flex items-center justify-center mb-3">
                <Users className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-muted-foreground">No friends yet. Search above to find people.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {summary.friends.map((u) => {
                const pos = u.net >= 0;
                return (
                  <div key={u.id} className="flex items-center justify-between py-3 group" data-testid={`friend-row-${u.id}`}>
                    <Link to={`/friends/${u.id}`} className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-90" data-testid={`friend-open-${u.id}`}>
                      <span className="h-10 w-10 rounded-full bg-foreground/5 border border-border flex items-center justify-center text-lg shrink-0">{u.avatar_emoji || "🙂"}</span>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                      </div>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      {Math.abs(u.net) > 0.005 && (
                        <span
                          className={`inline-flex items-center px-2.5 h-7 rounded-full text-xs font-mono tabular-nums ${pos ? "bg-[var(--brand)]/15 brand-text" : "bg-red-500/15 text-red-400"}`}
                          data-testid={`friend-net-${u.id}`}
                        >
                          {pos ? "+" : "-"}{currencySymbol(currency)}{Math.abs(u.net).toFixed(2)}
                        </span>
                      )}
                      <button
                        onClick={() => setSplitWith(u)}
                        className="h-8 px-3 rounded-md border border-border hover:bg-foreground/5 transition text-xs font-mono flex items-center gap-1"
                        data-testid={`friend-split-btn-${u.id}`}
                      >
                        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Split
                      </button>
                      {u.is_friend && (
                        <button onClick={() => remove(u)} className="text-muted-foreground hover:text-red-400 h-8 w-8 rounded-md flex items-center justify-center transition opacity-0 group-hover:opacity-100" data-testid={`remove-friend-btn-${u.id}`} aria-label="Remove friend">
                          <UserMinus className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      )}
                      <Link to={`/friends/${u.id}`} className="text-muted-foreground hover:text-foreground h-8 w-8 rounded-md flex items-center justify-center transition" data-testid={`friend-chevron-${u.id}`}>
                        <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <DirectSplitDialog open={!!splitWith} onOpenChange={(o) => !o && setSplitWith(null)} friend={splitWith} onSaved={() => { setSplitWith(null); load(); }} currency={currency} />
    </div>
  );
}
